import { apiClient } from './api-client';
import type { SlaPolicy } from '@centraoneplus/shared-types';

/**
 * A named organisational rung, resolved against the ticket when a level fires.
 * Mirrors `EscalationOrgTarget` in @centraoneplus/shared.
 */
export type EscalationOrgTarget = 'assignee' | 'team_lead' | 'manager' | 'department_head';

export interface EscalationAction {
  type: 'notify' | 'email' | 'assign' | 'priority_change' | 'webhook';
  config?: {
    targets?: EscalationOrgTarget[];
    channels?: string[];
    userIds?: string[];
    teamIds?: string[];
    roles?: string[];
    message?: string;
    [key: string]: unknown;
  };
}

/**
 * One rung of the escalation ladder.
 *
 * Index signature retained on purpose: the backend accepts several historical
 * rule shapes and normalizes them on read, so a policy authored over the API may
 * carry fields this UI does not render. They must survive a round-trip through
 * the settings form rather than being dropped.
 */
export interface EscalationRule {
  id?: string;
  level?: number;
  name?: string;
  isActive?: boolean;
  targetType: 'first_response' | 'next_response' | 'resolution';
  trigger: 'before_target' | 'at_breach' | 'after_breach' | 'percent_of_target';
  delayMinutes?: number;
  thresholdPercent?: number;
  actions?: EscalationAction[];
  [key: string]: unknown;
}

export interface CreateSlaPolicyDto {
  name: string;
  description?: string;
  isDefault?: boolean;
  isActive?: boolean;
  firstResponseTarget?: number;
  nextResponseTarget?: number;
  resolutionTarget?: number;
  /** Approaching-breach lead time (minutes) for first response; unset falls back to the built-in default. */
  firstResponseWarning?: number;
  /** Approaching-breach lead time (minutes) for resolution; unset falls back to the built-in default. */
  resolutionWarning?: number;
  useBusinessHours?: boolean;
  businessHoursId?: string | null;
  priorityTargets?: Record<string, { firstResponse: number; resolution: number }>;
  escalationRules?: EscalationRule[];
}

export interface UpdateSlaPolicyDto extends Partial<CreateSlaPolicyDto> {}

export interface SlaBreachFilters {
  startDate?: string;
  endDate?: string;
  breachType?: string;
  limit?: number;
}

/**
 * The ticket as the breach log carries it. `findBreachesByTenant` includes the
 * whole Ticket row, so the breach list needs no second round-trip to name what
 * breached — but every field stays optional, because a widened client type must
 * never assert more than the server actually promised.
 */
export interface SlaBreachTicket {
  id: string;
  ticketNumber?: string;
  subject?: string;
  status?: string;
  priority?: string;
  assigneeId?: string | null;
  customerId?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  firstResponseAt?: string | null;
  resolvedAt?: string | null;
}

/**
 * One row of `sla_breach_logs`.
 *
 * `targetTime` is the moment the clock ran out — a TIMESTAMP, despite the
 * name. `breachedAt` is when the monitor NOTICED, which is later by up to one
 * sweep interval, so an overrun is measured from `targetTime` and never from
 * `breachedAt`.
 */
export interface SlaBreach {
  id: string;
  ticketId: string;
  slaPolicyId?: string;
  /** Legacy alias, kept for callers written against the older shape. */
  policyId?: string;
  breachType: 'first_response' | 'next_response' | 'resolution';
  breachedAt: string;
  targetTime: string;
  metadata?: Record<string, unknown>;
  ticket?: SlaBreachTicket | null;
  slaPolicy?: { id: string; name: string } | null;
}

export interface SlaBreachStats {
  totalBreaches: number;
  firstResponseBreaches: number;
  nextResponseBreaches: number;
  resolutionBreaches: number;
  breachRate: number;
}

export const slaService = {
  // SLA Policy CRUD
  async createPolicy(data: CreateSlaPolicyDto): Promise<SlaPolicy> {
    const response = await apiClient.post('/sla/policies', data);
    return response.data;
  },

  async getPolicies(): Promise<SlaPolicy[]> {
    const response = await apiClient.get('/sla/policies');
    return response.data;
  },

  async getDefaultPolicy(): Promise<SlaPolicy | null> {
    try {
      const response = await apiClient.get('/sla/policies/default');
      return response.data;
    } catch {
      return null;
    }
  },

  async getPolicy(id: string): Promise<SlaPolicy> {
    const response = await apiClient.get(`/sla/policies/${id}`);
    return response.data;
  },

  async updatePolicy(id: string, data: UpdateSlaPolicyDto): Promise<SlaPolicy> {
    const response = await apiClient.put(`/sla/policies/${id}`, data);
    return response.data;
  },

  async deletePolicy(id: string): Promise<void> {
    await apiClient.delete(`/sla/policies/${id}`);
  },

  // Convenience methods
  async togglePolicyActive(id: string, isActive: boolean): Promise<SlaPolicy> {
    return this.updatePolicy(id, { isActive });
  },

  async setDefaultPolicy(id: string): Promise<SlaPolicy> {
    return this.updatePolicy(id, { isDefault: true });
  },

  // SLA Breaches
  async getBreaches(filters?: SlaBreachFilters): Promise<SlaBreach[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.breachType) params.append('breachType', filters.breachType);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`/sla/breaches?${params.toString()}`);
    return response.data;
  },

  async getTicketBreaches(ticketId: string): Promise<SlaBreach[]> {
    const response = await apiClient.get(`/sla/breaches/ticket/${ticketId}`);
    return response.data;
  },

  async getBreachStats(startDate?: string, endDate?: string): Promise<SlaBreachStats> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await apiClient.get(`/sla/breaches/stats?${params.toString()}`);
    return response.data;
  },
};

export default slaService;

// --------------------------------------------- escalation history (Task 8/9)

/** One escalation execution, as the admin history list renders it. */
export interface EscalationHistoryItem {
  id: string;
  ticketId: string;
  slaPolicyId: string | null;
  ruleName: string | null;
  level: number;
  targetType: string;
  dueAt: string;
  status: string;
  triggerReason: 'approaching_breach' | 'breached';
  executedAt: string | null;
  /** Who owned the ticket WHEN it escalated — not its current assignee. */
  previousAssigneeId: string | null;
  previousTeamId: string | null;
  elapsedBusinessMinutes: number | null;
  notificationStatus: string | null;
  lastError: string | null;
  ticket: {
    id: string;
    ticketNumber: string;
    subject: string;
    status: string;
    priority: string;
    customerId: string | null;
  } | null;
  slaPolicy: { id: string; name: string } | null;
}

export interface EscalationHistoryFilters {
  startDate?: string;
  endDate?: string;
  ticketId?: string;
  slaPolicyId?: string;
  level?: number;
  assigneeId?: string;
  teamId?: string;
  triggerReason?: 'approaching_breach' | 'breached';
  status?: string;
  targetType?: string;
  limit?: number;
  offset?: number;
}

export interface EscalationHistoryPage {
  items: EscalationHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface EscalationStats {
  total: number;
  byLevel: Record<string, number>;
  level1: number;
  level2: number;
  level3: number;
  approachingBreach: number;
  breached: number;
  byStatus: Record<string, number>;
  byAgent: Record<string, number>;
  byTeam: Record<string, number>;
}

export interface EscalationConfigIssue {
  code: string;
  severity: 'error' | 'warning';
  ruleIndex?: number;
  level?: number;
  targetType?: string;
  message: string;
}

export interface PolicyConfigHealth {
  policyId: string;
  policyName: string;
  isActive: boolean;
  isDefault: boolean;
  levelCount: number;
  inert: boolean;
  neverExecuted: boolean;
  executionCount: number;
  errorCount: number;
  warningCount: number;
  issues: EscalationConfigIssue[];
}

export interface EscalationConfigReport {
  policies: PolicyConfigHealth[];
  totalPolicies: number;
  inertPolicies: number;
  neverExecutedPolicies: number;
  policiesWithErrors: number;
}

/** Drop empty filters so the BFF never forwards a blank or "undefined" value. */
function escalationParams(filters: EscalationHistoryFilters): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params[key] = String(value);
  }
  return params;
}

export const escalationHistoryService = {
  async list(filters: EscalationHistoryFilters = {}): Promise<EscalationHistoryPage> {
    const { data } = await apiClient.get('/sla/escalations', { params: escalationParams(filters) });
    return data;
  },

  async stats(filters: EscalationHistoryFilters = {}): Promise<EscalationStats> {
    const { data } = await apiClient.get('/sla/escalations/stats', {
      params: escalationParams(filters),
    });
    return data;
  },

  async configReport(): Promise<EscalationConfigReport> {
    const { data } = await apiClient.get('/sla/escalations/config-report');
    return data;
  },

  async backfill(options: { limit?: number; dryRun?: boolean } = {}) {
    const { data } = await apiClient.post('/sla/escalations/backfill', null, {
      params: escalationParams(options as EscalationHistoryFilters),
    });
    return data as { scanned: number; matched: number; updated: number; unmatched: number; dryRun: boolean };
  },
};
